import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  TextField,
  Container,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Popover,
  Switch,
  Tooltip,
  useMediaQuery,
  IconButton,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth,
} from '@mui/icons-material';
import { Calendar } from 'react-date-range';
import { format, addDays, parseISO } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import PageHeader from './PageHeader';
import RecordingDayTimeline from './RecordingDayTimeline';
import { RecordingPlaybackModal } from './RecordingPlaybackModal';
import { authenticatedFetch, getCameraStreams, isRecordingWebPlayable } from '../utils/api';
import CloseIcon from '@mui/icons-material/Close';

const TIMELINE_DAYS_PER_PAGE = 7;

/** Local yyyy-MM-dd for a recording instant; matches RecordingDayTimeline (SQL `… …` → `T` before parsing). */
const recordingLocalDateKey = (iso) => {
  if (iso == null) return null;
  const t = String(iso).trim();
  if (!t) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(t) ? t.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T') : t;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'yyyy-MM-dd');
};

/** HH:mm (24h, from API / time input) -> e.g. "9:00 AM" for read-only schedule cards only. */
const formatScheduleTimeDisplay12h = (hhmm) => {
  if (hhmm == null || String(hhmm).trim() === '') return '—';
  const m = String(hhmm).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return String(hhmm);
  const h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h) || h < 0 || h > 23) return String(hhmm);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min}\u00A0${ampm}`;
};

/** Start `HH:mm` + duration (hours, may be fractional) → end `HH:mm` (24h), wrapped within a day. */
const computeScheduleEndTimeHhmm = (startHhmm, durationHours) => {
  const m = String(startHhmm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return null;
  const dur = parseFloat(durationHours);
  if (!Number.isFinite(dur) || dur < 0) return null;
  let totalMin = Math.round(h * 60 + min + dur * 60);
  totalMin = ((totalMin % (24 * 60)) + (24 * 60)) % (24 * 60);
  const eh = Math.floor(totalMin / 60);
  const em = totalMin % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

const formatScheduleEndDisplay12h = (startHhmm, durationHours) => {
  const end = computeScheduleEndTimeHhmm(startHhmm, durationHours);
  return end ? formatScheduleTimeDisplay12h(end) : '—';
};

/** Muted light red — shared by recordings table delete icon and delete-confirm modal button. */
const RECORDINGS_DELETE_RED = 'hsl(0, 56.70%, 49.80%)';
const RECORDINGS_DELETE_RED_HOVER_BG = 'hsla(0, 50%, 60%, 0.16)';
const RECORDINGS_DELETE_RED_BUTTON_HOVER = 'hsl(0, 50%, 52%)';

/** Date range accent — matches audit logs filters. */
const RECORDINGS_FILTER_PRIMARY = '#40518A';

const chipHueFromId = (id) => {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return ((h % 360) + 360) % 360;
};

/** Align with backend UUID rules (hyphenated or 32 hex chars). */
const normalizeCameraConfigId = (raw) => {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return null;
  if (
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(s)
  ) {
    return s.toLowerCase();
  }
  if (/^[0-9a-fA-F]{32}$/.test(s)) {
    const x = s.toLowerCase();
    return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`;
  }
  return null;
};

/**
 * Canonical id for schedules: prefer lowercase UUID formatting; fallback to trimmed DB id (≤36 chars)
 * so every camera listed by /camera-streams appears here (Lobby was skipped when strict UUID parse failed).
 */
const coerceRecordingCameraId = (raw) => {
  const n = normalizeCameraConfigId(raw);
  if (n) return n;
  const t = raw == null ? '' : String(raw).trim();
  if (!t || t.length > 36) return null;
  return t;
};

const dedupeNormalizedCameraIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (let i = 0; i < (ids?.length ?? 0); i += 1) {
    const c = coerceRecordingCameraId(ids[i]);
    if (!c) continue;
    const key = c.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(c);
    }
  }
  return out;
};

const RecordingInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /** @type {[{ startTime: string, durationHours: string, enabled: boolean, cameraConfigIds: string[] }]} */
  const [scheduleWindows, setScheduleWindows] = useState([]);
  const [configuredCameras, setConfiguredCameras] = useState([]);
  const [addSlotCameraIds, setAddSlotCameraIds] = useState([]);
  const [editingSlotIndex, setEditingSlotIndex] = useState(null);
  const [togglingSlotIndex, setTogglingSlotIndex] = useState(null);
  const [deletingSlotIndex, setDeletingSlotIndex] = useState(null);
  const [scheduleDeleteConfirmIndex, setScheduleDeleteConfirmIndex] = useState(null);
  const [addSlotModalOpen, setAddSlotModalOpen] = useState(false);
  const [addSlotStartTime, setAddSlotStartTime] = useState('09:00');
  const [addSlotDurationHours, setAddSlotDurationHours] = useState('2');
  const [addSlotSaving, setAddSlotSaving] = useState(false);
  const [slotModalSnackbar, setSlotModalSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(true);
  const [recordingTimelineDates, setRecordingTimelineDates] = useState([]);
  const [recordingsPagination, setRecordingsPagination] = useState({
    limit: TIMELINE_DAYS_PER_PAGE,
    offset: 0,
    current_page: 1,
    total: 0,
    total_pages: 0,
  });
  const [recordingsError, setRecordingsError] = useState(null);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  /** Bumps on each open so we can seek again when the same recording is chosen from the timeline. */
  const [playbackSessionId, setPlaybackSessionId] = useState(0);
  const [playInitialSeekSeconds, setPlayInitialSeekSeconds] = useState(null);
  const [deleteDayConfirmOpen, setDeleteDayConfirmOpen] = useState(false);
  const [dayPendingDelete, setDayPendingDelete] = useState(null);
  const [deleteDayLoading, setDeleteDayLoading] = useState(false);
  const [recordingsFilterByDate, setRecordingsFilterByDate] = useState(false);
  const [recordingsDateRange, setRecordingsDateRange] = useState(() => [
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  /** Popover-only selection; committed to `recordingsDateRange` on Apply so calendar clicks do not refetch. */
  const [recordingsDatePickerDraft, setRecordingsDatePickerDraft] = useState(() => [
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [recordingsDatePickerAnchor, setRecordingsDatePickerAnchor] = useState(null);
  const recordingsDatePickerOpen = Boolean(recordingsDatePickerAnchor);

  const applyScheduleResponse = useCallback((data) => {
    const fmtHm = (hour, minute) =>
      `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    // Always trust `windows` when API sends an array (including [] after last delete).
    // Do not fall back to legacy two-slot defaults when windows is empty — that was re-adding Slot 2.
    if (Array.isArray(data.windows)) {
      setScheduleWindows(
        data.windows.map((w) => ({
          startTime: fmtHm(w.hour, w.minute),
          durationHours: String(w.duration_hours ?? 2),
          enabled: w.enabled !== false,
          cameraConfigIds: dedupeNormalizedCameraIds(
            Array.isArray(w.camera_config_ids) ? w.camera_config_ids : [],
          ),
        }))
      );
      return;
    }
    setScheduleWindows([
      {
        startTime: data.window1_start_time ?? data.start_time ?? '09:00',
        durationHours: String(data.window1_duration_hours ?? data.duration_hours ?? 2),
        enabled: true,
        cameraConfigIds: [],
      },
      {
        startTime: data.window2_start_time ?? '14:00',
        durationHours: String(data.window2_duration_hours ?? 2),
        enabled: true,
        cameraConfigIds: [],
      },
    ]);
  }, []);

  const buildScheduleWindowsPayload = useCallback(() => {
    const parseHm = (hhmm) => {
      const m = String(hhmm || '09:00').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return { hour: 9, minute: 0 };
      return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
    };
    return scheduleWindows.map((sw) => {
      const h = parseHm(sw.startTime);
      return {
        ...h,
        duration_hours: parseFloat(sw.durationHours) || 2,
        enabled: sw.enabled !== false,
        camera_config_ids: dedupeNormalizedCameraIds(Array.isArray(sw.cameraConfigIds) ? sw.cameraConfigIds : []),
      };
    });
  }, [scheduleWindows]);

  const loadSchedule = useCallback(async () => {
    setScheduleError(null);
    setScheduleLoading(true);
    try {
      const res = await authenticatedFetch('/recording/schedule');
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load schedule');
      const data = await res.json();
      applyScheduleResponse(data);
    } catch (e) {
      setScheduleError(e.message || 'Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, [applyScheduleResponse]);

  const loadRecordingTimelinePage = useCallback(async (page = 1, options = {}) => {
    const { triggerScan = false, filters: filtersOpt } = options;
    const f = filtersOpt ?? {
      dateFrom: recordingsFilterByDate ? format(recordingsDateRange[0].startDate, 'yyyy-MM-dd') : null,
      dateTo: recordingsFilterByDate ? format(recordingsDateRange[0].endDate, 'yyyy-MM-dd') : null,
    };
    setRecordingsError(null);
    setRecordingsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(TIMELINE_DAYS_PER_PAGE),
      });
      if (triggerScan) {
        params.append('scan', 'true');
      }
      if (f.dateFrom) params.set('date_from', f.dateFrom);
      if (f.dateTo) params.set('date_to', f.dateTo);
      const res = await authenticatedFetch(`/recording/recordings/timeline-page?${params.toString()}`);
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load recordings');
      const data = await res.json();
      setRecordings(data.recordings || []);
      setRecordingTimelineDates(Array.isArray(data.dates) ? data.dates : []);
      const pag = data.pagination;
      setRecordingsPagination({
        limit: pag?.limit ?? TIMELINE_DAYS_PER_PAGE,
        offset: pag?.offset ?? 0,
        total: pag?.total ?? 0,
        total_pages: pag?.total_pages ?? 1,
        current_page: pag?.current_page ?? page,
      });
    } catch (e) {
      setRecordingsError(e.message || 'Failed to load recordings');
      setRecordings([]);
      setRecordingTimelineDates([]);
      setRecordingsPagination((prev) => ({ ...prev, total: 0, total_pages: 0 }));
    } finally {
      setRecordingsLoading(false);
    }
  }, [recordingsDateRange, recordingsFilterByDate]);

  const handleRecordingsPageChange = useCallback((_e, page) => {
    loadRecordingTimelinePage(page);
  }, [loadRecordingTimelinePage]);

  useEffect(() => {
    loadSchedule();
    loadRecordingTimelinePage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getCameraStreams();
        if (!cancelled) setConfiguredCameras(payload.items || []);
      } catch {
        if (!cancelled) setConfiguredCameras([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cameraLabelsById = useMemo(() => {
    const m = {};
    for (const c of configuredCameras) {
      const cid = coerceRecordingCameraId(c?.id);
      if (!cid) continue;
      const lb = (c.label || c.name || '').trim();
      m[cid] = lb || `Camera ${String(cid).slice(0, 8)}…`;
      m[cid.toLowerCase()] = m[cid];
    }
    return m;
  }, [configuredCameras]);

  /** Deduped cameras for multi-select — stable IDs for Autocomplete equality. */
  const cameraSelectOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of configuredCameras) {
      if (!c) continue;
      const id = coerceRecordingCameraId(c.id);
      if (!id) continue;
      const key = id.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ ...c, id });
    }
    return out;
  }, [configuredCameras]);

  /** Match slot Autocomplete: resolve label from options first, then label map (avoids raw UUID on prod). */
  const scheduleCameraChipLabel = useCallback(
    (cidRaw) => {
      const cid = coerceRecordingCameraId(cidRaw) || String(cidRaw || '').trim();
      if (!cid) return String(cidRaw ?? '').trim() || '—';
      const opt = cameraSelectOptions.find((c) => coerceRecordingCameraId(c.id) === cid);
      const fromOpt = (opt?.label || '').trim();
      if (fromOpt) return fromOpt;
      const mapped = cameraLabelsById[cid] ?? cameraLabelsById[String(cid).toLowerCase()];
      if (mapped) return mapped;
      return cid.length > 12 ? `Camera ${cid.slice(0, 8)}…` : cid;
    },
    [cameraSelectOptions, cameraLabelsById],
  );

  const handleOpenPlay = (rec, opts = {}) => {
    if (!isRecordingWebPlayable(rec)) return;
    const sk = opts?.seekSeconds;
    setPlayInitialSeekSeconds(
      sk != null && typeof sk === 'number' && Number.isFinite(sk) && sk >= 0 ? sk : null
    );
    setPlaybackSessionId((n) => n + 1);
    setSelectedRecording(rec);
    setPlayModalOpen(true);
  };

  const handleClosePlay = useCallback(() => {
    setPlayModalOpen(false);
    setSelectedRecording(null);
    setPlayInitialSeekSeconds(null);
  }, []);

  const putScheduleWindows = async (windows) => {
    const res = await authenticatedFetch('/recording/schedule', {
      method: 'PUT',
      body: JSON.stringify({ windows }),
    });
    return res.json();
  };

  const handleToggleSlot = async (index) => {
    setTogglingSlotIndex(index);
    setScheduleError(null);
    try {
      const windows = buildScheduleWindowsPayload();
      const cur = scheduleWindows[index];
      windows[index] = { ...windows[index], enabled: !(cur?.enabled !== false) };
      const data = await putScheduleWindows(windows);
      applyScheduleResponse(data);
    } catch (e) {
      setScheduleError(e.message || 'Failed to update slot');
    } finally {
      setTogglingSlotIndex(null);
    }
  };

  const handleDeleteSlot = async (index) => {
    setDeletingSlotIndex(index);
    setScheduleError(null);
    try {
      const next = buildScheduleWindowsPayload().filter((_, i) => i !== index);
      const data = await putScheduleWindows(next);
      applyScheduleResponse(data);
      setEditingSlotIndex((prev) => {
        if (prev === null) return null;
        if (prev === index) {
          setAddSlotModalOpen(false);
          return null;
        }
        if (prev > index) return prev - 1;
        return prev;
      });
    } catch (e) {
      setScheduleError(e.message || 'Failed to delete slot');
    } finally {
      setDeletingSlotIndex(null);
    }
  };

  const handleOpenScheduleDeleteConfirm = useCallback((index) => {
    setScheduleDeleteConfirmIndex(index);
  }, []);

  const handleCloseScheduleDeleteConfirm = useCallback(() => {
    if (deletingSlotIndex === null) {
      setScheduleDeleteConfirmIndex(null);
    }
  }, [deletingSlotIndex]);

  const handleConfirmScheduleDelete = useCallback(async () => {
    if (scheduleDeleteConfirmIndex == null) return;
    const idx = scheduleDeleteConfirmIndex;
    await handleDeleteSlot(idx);
    setScheduleDeleteConfirmIndex(null);
  }, [scheduleDeleteConfirmIndex]);

  const handleOpenAddSlotModal = () => {
    setEditingSlotIndex(null);
    setAddSlotStartTime('09:00');
    setAddSlotDurationHours('2');
    setAddSlotCameraIds([]);
    setAddSlotModalOpen(true);
  };

  const handleOpenEditSlotModal = (index) => {
    const slot = scheduleWindows[index];
    if (!slot) return;
    setEditingSlotIndex(index);
    setAddSlotStartTime(slot.startTime || '09:00');
    setAddSlotDurationHours(String(slot.durationHours ?? '2'));
    setAddSlotCameraIds(dedupeNormalizedCameraIds(slot.cameraConfigIds || []));
    setAddSlotModalOpen(true);
  };

  const handleCloseAddSlotModal = () => {
    if (!addSlotSaving) {
      setAddSlotModalOpen(false);
      setEditingSlotIndex(null);
      setSlotModalSnackbar((prev) => ({ ...prev, open: false }));
    }
  };

  const showSlotModalSnackbar = useCallback((message, severity = 'error') => {
    setSlotModalSnackbar({ open: true, message, severity });
  }, []);

  const handleAddSlotDurationChange = (event) => {
    const raw = event.target.value;
    if (raw === '' || raw === '-') {
      setAddSlotDurationHours(raw);
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) {
      setAddSlotDurationHours(raw);
      return;
    }
    if (n > 24) {
      setAddSlotDurationHours('24');
      showSlotModalSnackbar('Duration cannot be more than 24 hours', 'error');
      return;
    }
    setAddSlotDurationHours(raw);
  };

  const handleConfirmAddSlot = async () => {
    const parseHm = (hhmm) => {
      const m = String(hhmm || '09:00').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return { hour: 9, minute: 0 };
      return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
    };
    const dur = parseFloat(addSlotDurationHours);
    if (!Number.isFinite(dur) || dur <= 0) {
      showSlotModalSnackbar('Duration must be greater than 0', 'error');
      return;
    }
    if (dur > 24) {
      showSlotModalSnackbar('Duration cannot be more than 24 hours', 'error');
      return;
    }
    setAddSlotSaving(true);
    setScheduleError(null);
    try {
      const existing = buildScheduleWindowsPayload();
      const slotPayload = {
        ...parseHm(addSlotStartTime),
        duration_hours: dur,
        camera_config_ids: dedupeNormalizedCameraIds(addSlotCameraIds || []),
      };
      let nextWindows;
      if (editingSlotIndex != null && editingSlotIndex >= 0 && editingSlotIndex < existing.length) {
        nextWindows = existing.map((w, i) =>
          i === editingSlotIndex
            ? {
                ...slotPayload,
                enabled: scheduleWindows[editingSlotIndex]?.enabled !== false,
              }
            : w,
        );
      } else {
        nextWindows = [
          ...existing,
          {
            ...slotPayload,
            enabled: true,
          },
        ];
      }
      const data = await putScheduleWindows(nextWindows);
      applyScheduleResponse(data);
      setDeletingSlotIndex(null);
      setTogglingSlotIndex(null);
      setEditingSlotIndex(null);
      setAddSlotModalOpen(false);
    } catch (e) {
      showSlotModalSnackbar(
        e.message || (editingSlotIndex != null ? 'Failed to save slot' : 'Failed to add slot'),
        'error',
      );
    } finally {
      setAddSlotSaving(false);
    }
  };

  const scheduleEditButtonSx = { textTransform: 'none', fontWeight: 600 };
  /** Match /users active-user toggle: green on, grey off. */
  const slotEnabledSwitchSx = {
    '& .MuiSwitch-switchBase': {
      color: '#9e9e9e',
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#2e7d32',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#2e7d32',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#bdbdbd',
      opacity: 0.85,
    },
  };
  const scheduleDeleteTextButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    color: RECORDINGS_DELETE_RED,
    borderColor: RECORDINGS_DELETE_RED,
    '&:hover': {
      borderColor: RECORDINGS_DELETE_RED,
      color: RECORDINGS_DELETE_RED_BUTTON_HOVER,
      backgroundColor: RECORDINGS_DELETE_RED_HOVER_BG,
    },
  };

  const handleOpenDeleteDayConfirm = useCallback((dateKey, dateLabel) => {
    setDayPendingDelete({ dateKey, dateLabel });
    setDeleteDayConfirmOpen(true);
  }, []);

  const handleCloseDeleteDayConfirm = useCallback(() => {
    if (!deleteDayLoading) {
      setDeleteDayConfirmOpen(false);
      setDayPendingDelete(null);
    }
  }, [deleteDayLoading]);

  const reloadCurrentPage = useCallback(() => {
    return loadRecordingTimelinePage(recordingsPagination.current_page);
  }, [recordingsPagination.current_page, loadRecordingTimelinePage]);

  /**
   * Refresh the recordings index on demand to avoid automatic scan work on page open.
   * @returns {Promise<void>} Reloads the current page after asking the backend to scan once
   */
  const handleRefreshRecordings = useCallback(() => {
    return loadRecordingTimelinePage(recordingsPagination.current_page, { triggerScan: true });
  }, [recordingsPagination.current_page, loadRecordingTimelinePage]);

  const clearRecordingsFilters = useCallback(() => {
    setRecordingsFilterByDate(false);
    const cleared = [{ startDate: new Date(), endDate: new Date(), key: 'selection' }];
    setRecordingsDateRange(cleared);
    setRecordingsDatePickerDraft(cleared);
    loadRecordingTimelinePage(1, { filters: { dateFrom: null, dateTo: null } });
  }, [loadRecordingTimelinePage]);

  const handleRecordingsDatePopoverApply = useCallback(() => {
    const sel = recordingsDatePickerDraft[0];
    setRecordingsDateRange([{ startDate: sel.startDate, endDate: sel.endDate, key: 'selection' }]);
    setRecordingsFilterByDate(true);
    setRecordingsDatePickerAnchor(null);
    loadRecordingTimelinePage(1, {
      filters: {
        dateFrom: format(sel.startDate, 'yyyy-MM-dd'),
        dateTo: format(sel.endDate, 'yyyy-MM-dd'),
      },
    });
  }, [loadRecordingTimelinePage, recordingsDatePickerDraft]);

  const handleRecordingsDatePopoverReset = useCallback(() => {
    const today = new Date();
    const row = [{ startDate: today, endDate: today, key: 'selection' }];
    setRecordingsDateRange(row);
    setRecordingsDatePickerDraft(row);
    setRecordingsFilterByDate(false);
    setRecordingsDatePickerAnchor(null);
    loadRecordingTimelinePage(1, { filters: { dateFrom: null, dateTo: null } });
  }, [loadRecordingTimelinePage]);

  const handleConfirmDeleteDay = useCallback(async () => {
    if (!dayPendingDelete?.dateKey) return;
    setDeleteDayLoading(true);
    try {
      const qs = new URLSearchParams({ date: dayPendingDelete.dateKey });
      const res = await authenticatedFetch(`/recording/recordings/by-date?${qs.toString()}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete recordings for this day');
      }
      setDeleteDayConfirmOpen(false);
      setDayPendingDelete(null);
      await reloadCurrentPage();
    } catch (e) {
      setRecordingsError(e.message || 'Delete failed');
    } finally {
      setDeleteDayLoading(false);
    }
  }, [dayPendingDelete, reloadCurrentPage]);

  return (
    <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Recordings"
          subtitle="Schedule recording and view recent recordings"
        />

        {/* Recording schedule — table layout aligned with User Management */}
        {scheduleError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
            {scheduleError}
          </Alert>
        )}
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Schedule Recording
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenAddSlotModal}
              disabled={scheduleLoading || addSlotSaving}
              sx={{
                ...scheduleEditButtonSx,
                width: { xs: '100%', sm: 'auto' },
                px: 1.5,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              Add new slot
            </Button>
          </Box>

          {scheduleLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : scheduleWindows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No slots found. Add a slot to get started.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 100, whiteSpace: 'nowrap' }}>Slot</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 135, whiteSpace: 'nowrap' }}>Start time</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 115, whiteSpace: 'nowrap' }}>End time</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>Cameras to record</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 180, whiteSpace: 'nowrap' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduleWindows.map((slot, index) => {
                    const cameraIds = dedupeNormalizedCameraIds(slot.cameraConfigIds || []);

                    return (
                      <TableRow
                        key={`schedule-slot-${index}`}
                        hover
                        sx={{ opacity: slot.enabled !== false ? 1 : 0.72 }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Slot {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          {scheduleLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Tooltip title={slot.enabled !== false ? 'Disable slot' : 'Enable slot'} arrow>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                {togglingSlotIndex === index ? (
                                  <CircularProgress size={28} sx={{ mx: 0.5 }} />
                                ) : (
                                  <Switch
                                    checked={slot.enabled !== false}
                                    onChange={() => handleToggleSlot(index)}
                                    disabled={togglingSlotIndex === index}
                                    sx={slotEnabledSwitchSx}
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatScheduleTimeDisplay12h(slot.startTime)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatScheduleEndDisplay12h(slot.startTime, slot.durationHours)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {slot.durationHours}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                            {cameraIds.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No camera is selected
                              </Typography>
                            ) : (
                              cameraIds.map((cidRaw) => {
                                const cid = coerceRecordingCameraId(cidRaw) || String(cidRaw || '').trim();
                                return (
                                  <Chip
                                    key={cid}
                                    size="small"
                                    label={scheduleCameraChipLabel(cidRaw)}
                                    icon={
                                      <Box
                                        component="span"
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          bgcolor: `hsl(${chipHueFromId(cid)}, 68%, 48%)`,
                                          ml: 0.35,
                                        }}
                                      />
                                    }
                                    sx={{ '& .MuiChip-icon': { ml: 0.5 }, mr: 0.25 }}
                                  />
                                );
                              })
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ minWidth: 180, whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                        >
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenEditSlotModal(index)}
                              disabled={scheduleLoading || deletingSlotIndex === index || addSlotSaving}
                              size="small"
                              sx={scheduleEditButtonSx}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={deletingSlotIndex === index ? <CircularProgress size={16} /> : null}
                              onClick={() => handleOpenScheduleDeleteConfirm(index)}
                              disabled={scheduleLoading || deletingSlotIndex === index}
                              size="small"
                              sx={scheduleDeleteTextButtonSx}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>



        <Dialog open={addSlotModalOpen} onClose={handleCloseAddSlotModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {editingSlotIndex != null ? `Edit Slot ${editingSlotIndex + 1}` : 'Add new slot'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Start time"
                type="time"
                value={addSlotStartTime}
                onChange={(e) => setAddSlotStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              <TextField
                label="Duration (hours)"
                type="number"
                value={addSlotDurationHours}
                onChange={handleAddSlotDurationChange}
                inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={cameraSelectOptions}
                getOptionLabel={(o) => (o?.label || '').trim() || String(o?.id || '')}
                isOptionEqualToValue={(a, b) =>
                  coerceRecordingCameraId(a?.id) === coerceRecordingCameraId(b?.id)}
                value={dedupeNormalizedCameraIds(addSlotCameraIds).map((cid) => {
                  const n = coerceRecordingCameraId(cid) || String(cid || '').trim();
                  const hit = cameraSelectOptions.find((c) => coerceRecordingCameraId(c.id) === n);
                  return hit || { id: n, label: String(n).slice(0, 8) };
                })}
                onChange={(_, v) =>
                  setAddSlotCameraIds(
                    dedupeNormalizedCameraIds(v.map((x) => (typeof x === 'string' ? x : x?.id))),
                  )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: `hsl(${chipHueFromId(option.id)}, 68%, 48%)`,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" component="span">
                        {(option.label || '').trim() || option.id}
                      </Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cameras to record"
                    placeholder="Add cameras…"
                    size="small"
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseAddSlotModal} disabled={addSlotSaving} sx={scheduleEditButtonSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddSlot}
              disabled={addSlotSaving}
              startIcon={addSlotSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={slotModalSnackbar.open}
          autoHideDuration={2000}
          onClose={() => setSlotModalSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Alert
            onClose={() => setSlotModalSnackbar((prev) => ({ ...prev, open: false }))}
            severity={slotModalSnackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {slotModalSnackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={scheduleDeleteConfirmIndex != null}
          onClose={handleCloseScheduleDeleteConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete schedule slot?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              {scheduleDeleteConfirmIndex != null
                ? `This will permanently remove Slot ${scheduleDeleteConfirmIndex + 1}.`
                : 'This will permanently remove the selected slot.'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseScheduleDeleteConfirm}
              disabled={deletingSlotIndex != null}
              sx={scheduleEditButtonSx}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmScheduleDelete}
              disabled={deletingSlotIndex != null}
              startIcon={deletingSlotIndex != null ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                bgcolor: RECORDINGS_DELETE_RED,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: RECORDINGS_DELETE_RED_BUTTON_HOVER,
                  boxShadow: 'none',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {deletingSlotIndex != null ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recordings list */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.25, sm: 1 }, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Recent recordings
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefreshRecordings}
                disabled={recordingsLoading}
                startIcon={
                  recordingsLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  width: { xs: '100%', sm: 130 },
                  minWidth: { xs: '100%', sm: 130 },
                  maxWidth: { xs: '100%', sm: 130 },
                  px: 1,
                  justifyContent: 'center',
                  '& .MuiButton-startIcon': { ml: 0, mr: 0.5 },
                }}
              >
                {recordingsLoading ? 'Loading' : 'Refresh'}
              </Button>
            </Stack>
          </Box>

          {/* Date range filter */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
              flexWrap="wrap"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.85,
                  cursor: 'pointer',
                  minWidth: { xs: '100%', sm: 220 },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                  position: 'relative',
                }}
                onClick={(e) => {
                  setRecordingsDatePickerDraft(
                    recordingsDateRange.map((r) => ({
                      startDate: new Date(r.startDate),
                      endDate: new Date(r.endDate),
                      key: r.key,
                    }))
                  );
                  setRecordingsDatePickerAnchor(e.currentTarget);
                }}
              >
                <CalendarMonth fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {recordingsFilterByDate
                    ? format(recordingsDateRange[0].startDate, 'MMM dd, yyyy')
                    : 'All dates'}
                </Typography>
                {recordingsFilterByDate && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearRecordingsFilters();
                    }}
                    sx={{
                      ml: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Stack>

            <Popover
              open={recordingsDatePickerOpen}
              anchorEl={recordingsDatePickerAnchor}
              onClose={() => setRecordingsDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    maxWidth: '100vw',
                    width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
                    overflow: 'auto',
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      {
                        label: 'Today',
                        getValue: () => {
                          const d = new Date();
                          return { startDate: d, endDate: d };
                        },
                      },
                      {
                        label: 'Yesterday',
                        getValue: () => {
                          const d = addDays(new Date(), -1);
                          return { startDate: d, endDate: d };
                        },
                      },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setRecordingsDatePickerDraft([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    '& .rdrCalendarWrapper': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
                    '& .rdrMonth': { width: { xs: '280px', sm: '300px' } },
                  }}
                >
                  <Calendar
                    date={recordingsDatePickerDraft[0].startDate}
                    onChange={(d) => {
                      setRecordingsDatePickerDraft([{ startDate: d, endDate: d, key: 'selection' }]);
                    }}
                    color={RECORDINGS_FILTER_PRIMARY}
                    maxDate={new Date()}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0, flexWrap: 'wrap' }}>
                    <Button onClick={handleRecordingsDatePopoverReset} variant="outlined" size="small" startIcon={<RefreshIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Reset
                    </Button>
                    <Button onClick={() => setRecordingsDatePickerAnchor(null)} variant="outlined" size="small" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Cancel
                    </Button>
                    <Button onClick={handleRecordingsDatePopoverApply} variant="contained" size="small" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>

          {recordingsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecordingsError(null)}>
              {recordingsError}
            </Alert>
          )}
          {recordingsLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : recordingsPagination.total === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No recordings match the current filters.
            </Typography>
          ) : recordingTimelineDates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No timelines to show on this page.
            </Typography>
          ) : (
            <Stack spacing={2.5} sx={{ mb: 1 }}>
              {recordingTimelineDates.map((dk) => {
                const dayTitle = format(parseISO(`${dk}T12:00:00`), 'EEE, MMM dd, yyyy');
                return (
                  <RecordingDayTimeline
                    key={dk}
                    dateKey={dk}
                    dateLabel={dayTitle}
                    recordingsFromPage={recordings.filter((rec) => recordingLocalDateKey(rec.recorded_at || rec.start_time) === dk)}
                    configuredCameras={cameraSelectOptions}
                    onPlayRecording={handleOpenPlay}
                    onDeleteDay={() => handleOpenDeleteDayConfirm(dk, dayTitle)}
                  />
                );
              })}
            </Stack>
          )}
          {!recordingsLoading && recordingsPagination.total > 0 && (
            <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Days {recordingsPagination.offset + 1}–{Math.min(recordingsPagination.offset + recordingsPagination.limit, recordingsPagination.total)} of {recordingsPagination.total}
              </Typography>
              <Pagination
                count={recordingsPagination.total_pages}
                page={recordingsPagination.current_page}
                onChange={handleRecordingsPageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>

        <RecordingPlaybackModal
          open={playModalOpen}
          recording={selectedRecording}
          onClose={handleClosePlay}
          initialSeekSeconds={playInitialSeekSeconds}
          playbackSessionId={playbackSessionId}
          configuredCameras={configuredCameras}
        />

        {/* Delete all recordings for a calendar day */}
        <Dialog
          open={deleteDayConfirmOpen}
          onClose={handleCloseDeleteDayConfirm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogContent sx={{ p: 3, pt: 3 }}>
            {dayPendingDelete && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Delete every recording for this day?
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5, color: 'text.primary' }}>
                  {dayPendingDelete.dateLabel}{' '}
                  {/* <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ({dayPendingDelete.dateKey})
                  </Typography> */}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  All video recordings on this calendar day will be removed.
                </Typography>
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
            <Button
              onClick={handleCloseDeleteDayConfirm}
              variant="outlined"
              disabled={deleteDayLoading}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDeleteDay}
              disabled={deleteDayLoading}
              startIcon={deleteDayLoading ? <CircularProgress size={16} color="inherit" /> : null}
              fullWidth={isMobile}
              sx={{
                bgcolor: RECORDINGS_DELETE_RED,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: RECORDINGS_DELETE_RED_BUTTON_HOVER,
                  boxShadow: 'none',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {deleteDayLoading ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default RecordingInterface;